<template>
  <div
    class="accessible-form-field"
    :class="fieldClasses"
  >
    <!-- Field Label -->
    <label
      v-if="label"
      :for="fieldId"
      :class="labelClasses"
      class="accessible-form-label"
    >
      {{ label }}
      <span
        v-if="required"
        class="required-indicator"
        aria-label="required field"
      >
        *
      </span>
    </label>

    <!-- Help Text (before input for screen readers) -->
    <div
      v-if="helpText"
      :id="`${fieldId}-help`"
      class="form-help-text"
      role="note"
    >
      {{ helpText }}
    </div>

    <!-- Input Field -->
    <component
      :is="fieldComponent"
      :id="fieldId"
      v-model="internalValue"
      :class="inputClasses"
      v-bind="inputAttributes"
      :aria-required="required"
      :aria-invalid="hasError"
      :aria-describedby="describedByIds"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
      @keydown="handleKeydown"
    >
      <!-- Slot for select options or other content -->
      <slot />
    </component>

    <!-- Error Messages -->
    <div
      v-if="hasError"
      :id="`${fieldId}-error`"
      class="form-error-message"
      role="alert"
      aria-live="assertive"
    >
      <v-icon
        small
        class="error-icon"
        aria-hidden="true"
      >
        fas fa-exclamation-triangle
      </v-icon>
      {{ errorMessage }}
    </div>

    <!-- Success Message -->
    <div
      v-if="hasSuccess"
      :id="`${fieldId}-success`"
      class="form-success-message"
      role="status"
      aria-live="polite"
    >
      <v-icon
        small
        class="success-icon"
        aria-hidden="true"
      >
        fas fa-check-circle
      </v-icon>
      {{ successMessage }}
    </div>

    <!-- Character Count (for text inputs) -->
    <div
      v-if="showCharacterCount && maxLength"
      :id="`${fieldId}-count`"
      class="character-count"
      :class="{ 'near-limit': characterCountNearLimit }"
      aria-live="polite"
    >
      {{ characterCount }}/{{ maxLength }} characters
      <span class="sr-only">
        {{ characterCount === maxLength ? 'Character limit reached' : '' }}
      </span>
    </div>
  </div>
</template>

<script>
import { generateId } from '@/utils/global.utils';

export default {
  name: 'AccessibleFormField',

  props: {
    // Field configuration
    type: {
      type: String,
      default: 'text',
      validator: value => ['text', 'email', 'password', 'tel', 'url', 'number', 'textarea', 'select'].includes(value)
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number, Boolean],
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },

    // Validation
    rules: {
      type: Array,
      default: () => []
    },
    errorMessage: {
      type: String,
      default: ''
    },
    successMessage: {
      type: String,
      default: ''
    },

    // Help and description
    helpText: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },

    // Text field specific
    maxLength: {
      type: Number,
      default: null
    },
    showCharacterCount: {
      type: Boolean,
      default: false
    },

    // Autocomplete
    autocomplete: {
      type: String,
      default: ''
    },

    // Styling
    dense: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: true
    },

    // Accessibility
    fieldId: {
      type: String,
      default: () => generateId('field')
    }
  },
emits: {'focus': null, 'input': null, 'input-event': null, 'blur': null, 'keydown': null, 'validation-change': null},

  data() {
    return {
      internalValue: this.value,
      isFocused: false,
      hasBeenBlurred: false,
      validationErrors: []
    };
  },

  computed: {
    /**
     * Determine the component to render for the input
     */
    fieldComponent() {
      switch (this.type) {
        case 'textarea':
          return 'v-textarea';
        case 'select':
          return 'v-select';
        default:
          return 'v-text-field';
      }
    },

    /**
     * Get input attributes based on type
     */
    inputAttributes() {
      const attrs = {
        type: this.type === 'textarea' || this.type === 'select' ? undefined : this.type,
        placeholder: this.placeholder,
        disabled: this.disabled,
        readonly: this.readonly,
        dense: this.dense,
        outlined: this.outlined,
        'hide-details': true // We handle validation display ourselves
      };

      // Add autocomplete
      if (this.autocomplete) {
        attrs.autocomplete = this.autocomplete;
      } else {
        // Set appropriate autocomplete based on type
        const autocompleteMap = {
          'email': 'email',
          'password': 'current-password',
          'tel': 'tel',
          'url': 'url'
        };
        if (autocompleteMap[this.type]) {
          attrs.autocomplete = autocompleteMap[this.type];
        }
      }

      // Add maxlength for text inputs
      if (this.maxLength && ['text', 'email', 'tel', 'url', 'textarea'].includes(this.type)) {
        attrs.maxlength = this.maxLength;
      }

      return attrs;
    },

    /**
     * Calculate described by IDs for ARIA
     */
    describedByIds() {
      const ids = [];

      if (this.helpText) {
        ids.push(`${this.fieldId}-help`);
      }

      if (this.hasError) {
        ids.push(`${this.fieldId}-error`);
      }

      if (this.hasSuccess) {
        ids.push(`${this.fieldId}-success`);
      }

      if (this.showCharacterCount && this.maxLength) {
        ids.push(`${this.fieldId}-count`);
      }

      return ids.join(' ') || null;
    },

    /**
     * Check if field has validation errors
     */
    hasError() {
      return (this.errorMessage && this.errorMessage.length > 0) ||
             (this.validationErrors.length > 0 && this.hasBeenBlurred);
    },

    /**
     * Check if field has success state
     */
    hasSuccess() {
      return this.successMessage && this.successMessage.length > 0 &&
             !this.hasError && this.hasBeenBlurred;
    },

    /**
     * Get current character count
     */
    characterCount() {
      return String(this.internalValue || '').length;
    },

    /**
     * Check if character count is near limit
     */
    characterCountNearLimit() {
      if (!this.maxLength) return false;
      return this.characterCount >= this.maxLength * 0.9;
    },

    /**
     * CSS classes for the field container
     */
    fieldClasses() {
      return {
        'field-focused': this.isFocused,
        'field-error': this.hasError,
        'field-success': this.hasSuccess,
        'field-required': this.required,
        'field-disabled': this.disabled,
        'field-dense': this.dense
      };
    },

    /**
     * CSS classes for the label
     */
    labelClasses() {
      return {
        'label-focused': this.isFocused,
        'label-error': this.hasError,
        'label-success': this.hasSuccess
      };
    },

    /**
     * CSS classes for the input
     */
    inputClasses() {
      return {
        'form-field-error': this.hasError,
        'form-field-success': this.hasSuccess
      };
    }
  },

  watch: {
    value(newValue) {
      this.internalValue = newValue;
    },

    internalValue(newValue) {
      this.$emit('input', newValue);
      this.validateField();
    }
  },

  mounted() {
    // Initial validation if there's a value
    if (this.value) {
      this.validateField();
    }
  },

  methods: {
    /**
     * Handle input focus
     */
    handleFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
    },

    /**
     * Handle input blur
     */
    handleBlur(event) {
      this.isFocused = false;
      this.hasBeenBlurred = true;
      this.validateField();
      this.$emit('blur', event);
    },

    /**
     * Handle input change
     */
    handleInput(event) {
      this.$emit('input-event', event);
    },

    /**
     * Handle keydown events
     */
    handleKeydown(event) {
      // Announce character limit when approaching
      if (this.maxLength && this.showCharacterCount) {
        const remaining = this.maxLength - this.characterCount;
        if (remaining <= 10 && remaining > 0 && event.key.length === 1) {
          // Only announce for actual character input
          setTimeout(() => {
            if (this.characterCount >= this.maxLength - 5) {
              this.announceCharacterLimit(remaining);
            }
          }, 100);
        }
      }

      this.$emit('keydown', event);
    },

    /**
     * Validate the field based on rules
     */
    validateField() {
      if (!this.rules || this.rules.length === 0) {
        this.validationErrors = [];
        return;
      }

      const errors = [];
      const value = this.internalValue;

      this.rules.forEach(rule => {
        const result = typeof rule === 'function' ? rule(value) : rule;
        if (result !== true && typeof result === 'string') {
          errors.push(result);
        }
      });

      this.validationErrors = errors;

      // Emit validation state
      this.$emit('validation-change', {
        isValid: errors.length === 0,
        errors: errors
      });
    },

    /**
     * Announce character limit to screen readers
     */
    announceCharacterLimit(remaining) {
      const message = remaining === 0
        ? 'Character limit reached'
        : `${remaining} characters remaining`;

      // Create temporary live region for announcement
      const announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      announcer.textContent = message;

      document.body.appendChild(announcer);

      setTimeout(() => {
        if (document.body.contains(announcer)) {
          document.body.removeChild(announcer);
        }
      }, 1000);
    },

    /**
     * Focus the input field
     */
    focus() {
      this.$nextTick(() => {
        const input = this.$el.querySelector('input, textarea, select');
        if (input && input.focus) {
          input.focus();
        }
      });
    },

    /**
     * Get current validation state
     */
    getValidationState() {
      return {
        isValid: !this.hasError,
        errors: this.validationErrors,
        value: this.internalValue
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~/_variables.scss';

.accessible-form-field {
  margin-bottom: 1rem;

  &.field-dense {
    margin-bottom: 0.5rem;
  }
}

.accessible-form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: $white;
  font-size: 0.875rem;
  line-height: 1.4;

  &.label-focused {
    color: $accent;
  }

  &.label-error {
    color: $error;
  }

  &.label-success {
    color: $success;
  }
}

.required-indicator {
  color: $error;
  margin-left: 0.25rem;
  font-weight: bold;
}

.form-help-text {
  font-size: 0.75rem;
  color: rgba($white, 0.7);
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.form-error-message {
  display: flex;
  align-items: center;
  color: $error;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  line-height: 1.4;

  .error-icon {
    margin-right: 0.25rem;
    flex-shrink: 0;
  }
}

.form-success-message {
  display: flex;
  align-items: center;
  color: $success;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  line-height: 1.4;

  .success-icon {
    margin-right: 0.25rem;
    flex-shrink: 0;
  }
}

.character-count {
  font-size: 0.75rem;
  color: rgba($white, 0.6);
  text-align: right;
  margin-top: 0.25rem;

  &.near-limit {
    color: $warning;
    font-weight: 500;
  }
}

// Touch target enhancement
:deep(.v-input__control) {
  min-height: 44px; // WCAG AA requirement
}

:deep(.v-text-field__slot) {
  min-height: 44px;
  align-items: center;
}

// Focus enhancement
:deep(.v-text-field.v-input--is-focused .v-input__control) {
  box-shadow: 0 0 0 2px $accent;
}

// Error state styling
.field-error {
  :deep(.v-text-field .v-input__control) {
    border-color: $error !important;
  }

  :deep(.v-text-field.v-input--is-focused .v-input__control) {
    box-shadow: 0 0 0 2px $error;
  }
}

// Success state styling
.field-success {
  :deep(.v-text-field .v-input__control) {
    border-color: $success !important;
  }

  :deep(.v-text-field.v-input--is-focused .v-input__control) {
    box-shadow: 0 0 0 2px $success;
  }
}

// High contrast mode
@media (prefers-contrast: high) {
  .accessible-form-label {
    color: #ffffff !important;
    font-weight: bold;
  }

  .form-help-text {
    color: #ffffff !important;
  }

  .form-error-message {
    background: $error !important;
    color: #ffffff !important;
    padding: 0.25rem;
    border-radius: 2px;
  }

  .form-success-message {
    background: $success !important;
    color: #ffffff !important;
    padding: 0.25rem;
    border-radius: 2px;
  }
}

// Reduced motion
@media (prefers-reduced-motion: reduce) {
  :deep(.v-text-field) {
    transition: none !important;
  }

  .character-count {
    transition: none !important;
  }
}

// Print styles
@media print {
  .form-help-text,
  .character-count {
    display: none;
  }

  .accessible-form-label {
    color: #000000 !important;
  }
}
</style>
